import type { ComparisonRow, PDPContent, QuickFact } from '@/lib/productContent';
import { PageSection } from './ConversionSections';

/**
 * At-a-glance summary, rendered as collapsible dropdown rows.
 * Uses native <details> so every answer is present in the initial HTML
 * (crawlers / no-JS readers) while staying collapsed for visitors.
 */
export function TldrPanel({ tldr }: { tldr: PDPContent['tldr'] | undefined }) {
  if (!tldr) return null;
  const rows = [
    { label: 'What it is', value: tldr.what },
    { label: "Who it's for", value: tldr.who },
    { label: 'Primary benefit', value: tldr.benefit, accent: true },
  ].filter(r => Boolean(r.value));
  if (!rows.length) return null;
  return (
    <dl className="twc-tldr">
      {rows.map(r => (
        <details
          key={r.label}
          name="twc-tldr"
          className={`${r.accent ? 'twc-tldr-row is-accent' : 'twc-tldr-row'} is-collapsible`}
        >
          <summary className="twc-tldr-trigger">
            <dt>{r.label}</dt>
            <svg
              className="twc-tldr-chevron"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path d="M3.5 6l4.5 4.5L12.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </summary>
          <dd>{r.value}</dd>
        </details>
      ))}
    </dl>
  );
}

/** The label, summarised. */
export function FactsPanel({ facts }: { facts: QuickFact[] | undefined }) {
  if (!facts?.length) return null;
  return (
    <PageSection id="twc-facts" eyebrow="Quick facts" title="The label, summarised" engagement="quick-facts">
      <dl className="twc-facts">
        {facts.map(f => (
          <div key={f.label}>
            <dt>{f.label}</dt>
            <dd>{f.value}</dd>
          </div>
        ))}
      </dl>
    </PageSection>
  );
}

/** TruWe vs standard supplements. */
export function ComparePanel({ rows, productName }: { rows: ComparisonRow[] | undefined; productName: string }) {
  if (!rows?.length) return null;
  return (
    <PageSection id="twc-compare" eyebrow="How it compares" title={`${productName} vs standard supplements`} engagement="comparison">
      <div className="twc-compare" role="table" aria-label={`${productName} compared with standard supplements`}>
        <div className="twc-compare-head" role="row">
          <span role="columnheader">Metric</span>
          <span role="columnheader">TruWe</span>
          <span role="columnheader">Standard</span>
        </div>
        {rows.map(r => (
          <div className="twc-compare-row" role="row" key={r.metric}>
            <span role="cell" data-label="Metric">{r.metric}</span>
            <span role="cell" data-label="TruWe">{r.truwe}</span>
            <span role="cell" data-label="Standard">{r.standard}</span>
          </div>
        ))}
      </div>
    </PageSection>
  );
}
